import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@cargomatic/ui';
import { RefreshCw, X } from 'lucide-react';
import PDFViewer from '../../shipments/PDFViewer';
import ImageViewer from '../../shipments/ImageViewer';

export function DocumentPreviewModal({ document, onClose, dialogClassName }) {
  const { t } = useTranslation();
  const [signUrl, setSignUrl] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [paginationContainer, setPaginationContainer] = useState(null);

  const initDocumentPreview = async () => {
    setRotation(0);

    if (document?.signedUrl) {
      setSignUrl(document.signedUrl);
    } else {
      setSignUrl(null);
    }
  };

  useEffect(() => {
    initDocumentPreview();
  }, [document]);

  const handleRotation = () => {
    setRotation(rotation + 90);
  };

  const renderDocumentTypeViewer = (document) => {
    const { contentType } = document || {};
    const supportedImageTypes = ['image/png', 'image/jpg', 'image/jpeg'];

    if (contentType === 'application/pdf') {
      return (
        <PDFViewer
          pdfStyle={{ transform: `rotate(${rotation}deg)` }}
          file={signUrl}
          height="auto"
          width="auto"
          paginationContainer={paginationContainer}
        />
      );
    }
    if (supportedImageTypes.includes(contentType)) {
      return (
        <ImageViewer
          alt="document preview"
          className="w-full h-full"
          fileUrl={signUrl}
          style={{ transform: `rotate(${rotation}deg)` }}
        />
      );
    }
    return <span>{t('DOCUMENT_NOT_SUPPORTED')}</span>;
  };

  return (
    <Dialog
      open={!!document}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      modal={false}
    >
      <DialogContent
        className={
          dialogClassName ||
          'max-w-3xl top-[10%] translate-y-0 max-h-[85vh] overflow-y-auto'
        }
      >
        <DialogHeader>
          <DialogTitle className="flex justify-between items-center w-full">
            <span className="flex-grow text-sm font-normal">
              {document &&
                document.createdAt &&
                moment(document.createdAt).format('M/D/YYYY h:mm a')}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleRotation}
                aria-label="rotation button"
              >
                <RefreshCw className="size-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                aria-label="close button"
              >
                <X className="size-5" />
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>
        <div className="min-h-[70vh] flex flex-col">
          {signUrl && (
            <div className="flex-grow flex flex-col items-center">
              {renderDocumentTypeViewer(document)}
            </div>
          )}
        </div>
        <DialogFooter className="sm:justify-start">
          <div ref={setPaginationContainer} className="flex flex-1" />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

DocumentPreviewModal.propTypes = {
  document: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  dialogClassName: PropTypes.string,
};
